import {
  ChatroomDeclineAccess,
  ChatroomGrantAccess,
  ChatroomRequestAccess,
  MessageStatusUpdateSocket,
  OutgoingSocketEvents,
  ReceiveSocketMessage,
  SendSocketMessage,
  SocketMessage,
  SocketResponse,
} from '@connect-x-app/shared-domain-models';
import { AppConfig } from '@connect-x-app/shared-types';
import { io, Socket } from 'socket.io-client';

import { getHeaders } from '../utils';

let socket: Socket | null = null;
let appConfig: AppConfig;
let socketHostUrl: string = '';

export const getSocket = (hostUrl: string, appConfiguration: AppConfig) => {
  if (!socket) {
    appConfig = appConfiguration;
    socketHostUrl = hostUrl;
    socket = io(socketHostUrl, {
      ackTimeout: 60000,
      autoConnect: false,
      extraHeaders: getHeaders(appConfig),
      path: '/api/v3/connect',
      // in browser the extraHeaders is not supported, so we pass the information this way
      query: Object.fromEntries(Object.entries(getHeaders(appConfig)).map(([key, value]) => [key.toLowerCase(), value])),
      transports: ['websocket'],
      withCredentials: true,
    });
  }
  return socket;
};

const sendOverSocketWithAck = async <T, R = T>(event: keyof OutgoingSocketEvents, data?: T): Promise<SocketResponse<R>> => {
  try {
    return (await getSocket(socketHostUrl, appConfig).emitWithAck(event, data)) as SocketResponse<R>;
  } catch {
    // socket.io rejects once its ackTimeout passes without an answer: the server never took the emit, so it's refused.
    return { ok: false };
  }
};

const sendOverSocket = <T>(event: keyof OutgoingSocketEvents, data?: T) => {
  getSocket(socketHostUrl, appConfig).emit(event, data);
};

export const sendChatSendMessageEvent = (message: SendSocketMessage) => {
  return sendOverSocketWithAck<SendSocketMessage, ReceiveSocketMessage>('chat.send-message', message);
};

export const sendChatroomRequestAccessEvent = (payload: ChatroomRequestAccess) => {
  return sendOverSocketWithAck<ChatroomRequestAccess, void>('chat.chatroom-request-access', payload);
};

export const sendChatroomGrantAccessEvent = (payload: ChatroomGrantAccess) => {
  return sendOverSocketWithAck<ChatroomGrantAccess, void>('chat.chatroom-grant-access', payload);
};

export const sendChatroomDeclineAccessEvent = (payload: ChatroomDeclineAccess) => {
  return sendOverSocketWithAck<ChatroomDeclineAccess, void>('chat.chatroom-decline-access', payload);
};

export const sendChatDeliverMessageEvent = (payload: MessageStatusUpdateSocket) => {
  sendOverSocket('chat.deliver-message', payload);
};

export const sendChatReadMessageEvent = (payload: MessageStatusUpdateSocket) => {
  sendOverSocket('chat.read-message', payload);
};

export const sendChatReadAllMessagesEvent = (payload: SocketMessage) => {
  sendOverSocket('chat.read-all-messages', payload);
};

export const sendChatJoinChannelEvent = (chatroomId: string, channelId: string) => {
  return sendOverSocketWithAck('chat.join-channel', { channelId, chatroomId });
};

export const sendChatJoinRoomEvent = (chatroomId: string) => {
  return sendOverSocketWithAck('chat.join-chatroom', { chatroomId });
};

export const sendChatLeaveChannelEvent = (chatroomId: string, channelId: string) => {
  return sendOverSocketWithAck('chat.leave-channel', { channelId, chatroomId });
};

export const sendChatLeaveRoomEvent = (chatroomId: string) => {
  return sendOverSocketWithAck('chat.leave-chatroom', { chatroomId });
};

export const sendNotificationDismissEvent = (notificationId: string) => {
  return sendOverSocketWithAck<{ notificationId: string }, void>('notifications.dismiss-notification', { notificationId });
};
